"use client";
import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useAuth } from "@/context/AuthContext";
import ThemeSelector from "./ThemeSelector";
import { ChevronDown, ChevronRight, LogOut, Mail, Megaphone, MessageSquareText, Settings, Shield, ShoppingBag, SunMoon, TicketPercent, Truck, User } from "lucide-react";
import { getCartSummaryAction } from "@/app/actions/cartActions";
import type { InformationMenuItem } from "@/lib/staticPageMenu";

interface HeaderProps {
  controlPlane?: boolean;
  informationMenu: InformationMenuItem[];
}

export const Header: React.FC<HeaderProps> = ({ controlPlane = false, informationMenu }) => {
  const { user, token, permissions, login, signup, logout } = useAuth();
  const [cartCount, setCartCount] = useState(0);
  const [newInquiryCount, setNewInquiryCount] = useState(0);
  const [unreadEmailCount, setUnreadEmailCount] = useState(0);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isInformationOpen, setIsInformationOpen] = useState(false);
  const [openInformationSection, setOpenInformationSection] = useState<string | null>(null);
  const [isMobileInformationOpen, setIsMobileInformationOpen] = useState(false);
  const [openMobileSection, setOpenMobileSection] = useState<string | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const informationCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const canEditProducts = permissions.canManageCatalog();
  const canEditSiteSettings = permissions.canManageSiteSettings();
  const canManageInquiries = permissions.canManageInquiries();
  const canViewEmail = permissions.canAccessEmail();
  const canViewAdminMenu = canEditProducts || canEditSiteSettings;

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsAdminOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsAdminOpen(false);
    }, 300); // Remain open for 0.3 seconds after mouse leaves the region
  };

  const openInformationMenu = () => {
    if (informationCloseTimeoutRef.current) {
      clearTimeout(informationCloseTimeoutRef.current);
      informationCloseTimeoutRef.current = null;
    }
    setIsInformationOpen(true);
  };

  const scheduleInformationMenuClose = () => {
    informationCloseTimeoutRef.current = setTimeout(() => {
      setIsInformationOpen(false);
      setOpenInformationSection(null);
    }, 250);
  };

  const closeInformationMenu = () => {
    setIsInformationOpen(false);
    setOpenInformationSection(null);
  };

  // Sync cart count on mount and when updated, and clean up timeouts
  useEffect(() => {
    let isActive = true;

    const refreshCartCount = async () => {
      const summary = await getCartSummaryAction();
      if (isActive) {
        setCartCount(summary.count);
      }
    };

    refreshCartCount();

    const handleCartUpdate = () => {
      refreshCartCount();
    };

    window.addEventListener("cart-updated", handleCartUpdate);
    return () => {
      isActive = false;
      window.removeEventListener("cart-updated", handleCartUpdate);
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (informationCloseTimeoutRef.current) {
        clearTimeout(informationCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isAccountOpen) return;

    const closeOnOutsidePointer = (event: MouseEvent) => {
      if (!accountMenuRef.current?.contains(event.target as Node)) {
        setIsAccountOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsAccountOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isAccountOpen]);

  useEffect(() => {
    const updateCount = (event: Event) => {
      const delta = Number((event as CustomEvent<{ delta?: number }>).detail?.delta) || 0;
      setNewInquiryCount((current) => Math.max(0, current + delta));
    };
    window.addEventListener("axiom-inquiry-count-change", updateCount);
    return () => window.removeEventListener("axiom-inquiry-count-change", updateCount);
  }, []);

  useEffect(() => {
    if (!token || !user) {
      setNewInquiryCount(0);
      setUnreadEmailCount(0);
      return;
    }
    const userEmail = user.email;
    let active = true;

    async function refreshCounts() {
      const headers = { Authorization: `Bearer ${token}` };
      if (canManageInquiries) {
        try {
          const response = await fetch("/api/admin/inquiries/count", { headers, cache: "no-store" });
          const payload = await response.json() as { count?: unknown };
          if (active && response.ok) setNewInquiryCount(Number(payload.count) || 0);
        } catch {
          // Preserve the last successful badge value during a temporary outage.
        }
      } else if (active) {
        setNewInquiryCount(0);
      }

      if (canViewEmail && userEmail) {
        try {
          const mailboxResponse = await fetch("/api/email-api/email/mailboxes", { headers, cache: "no-store" });
          const mailboxes = await mailboxResponse.json() as Array<{ id?: unknown; address?: unknown }>;
          const mailbox = Array.isArray(mailboxes)
            ? mailboxes.find((candidate) => typeof candidate.address === "string" && candidate.address.toLowerCase() === userEmail.toLowerCase())
            : null;
          if (!mailboxResponse.ok || typeof mailbox?.id !== "string") return;
          const foldersResponse = await fetch(`/api/email-api/email/mailboxes/${encodeURIComponent(mailbox.id)}/folders`, { headers, cache: "no-store" });
          const folders = await foldersResponse.json() as Array<{ folder_key?: unknown; unread_count?: unknown }>;
          const inbox = Array.isArray(folders) ? folders.find((folder) => folder.folder_key === "inbox") : null;
          if (active && foldersResponse.ok) setUnreadEmailCount(Number(inbox?.unread_count) || 0);
        } catch {
          // Preserve the last successful badge value during a temporary outage.
        }
      } else if (active) {
        setUnreadEmailCount(0);
      }
    }

    void refreshCounts();
    const interval = window.setInterval(() => { void refreshCounts(); }, 5 * 60 * 1000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [canManageInquiries, canViewEmail, token, user]);

  return (
    <header className={`sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border transition-colors duration-300${controlPlane ? " control-plane-header" : ""}`}>
      <div className="layout-container h-20 flex items-center justify-between">
        {/* Brand Logo & Name */}
        <Link href="/" className="flex items-center gap-3 group">
          <div className="flex flex-col">
            <span className="text-sm font-semibold uppercase tracking-[0.25em] text-foreground">
              Axiom Manifold
            </span>
            <span className="text-[9px] uppercase tracking-widest text-foreground/50 font-medium -mt-0.5">
              {controlPlane ? "Administrative Control Plane" : "No Scarcity. Only Creation."}
            </span>
          </div>
        </Link>

        {/* Navigation links */}
        {controlPlane && (
          <nav aria-label="Administrative applications" className="control-plane-nav hidden md:flex items-center text-xs uppercase tracking-widest font-medium">
            {canEditProducts && <Link href="/admin"><SunMoon />Catalog</Link>}
            {canEditProducts && <Link href="/admin/shipping-rates"><Truck />Shipping</Link>}
            {canEditSiteSettings && <Link href="/admin/site-settings"><Settings />Settings</Link>}
            {canEditSiteSettings && <Link href="/admin/campaigns"><Megaphone />Campaigns</Link>}
            {canEditSiteSettings && <Link href="/admin/coupons"><TicketPercent />Coupons</Link>}
          </nav>
        )}

        <nav className={`${controlPlane ? "hidden" : "hidden md:flex"} items-center gap-8 text-xs uppercase tracking-widest font-medium`}>
          <Link href="/catalog" className="text-foreground/80 hover:text-foreground hover:underline underline-offset-4 transition">
            Shop Fragrances
          </Link>
          <Link href="/brands" className="text-foreground/80 hover:text-foreground hover:underline underline-offset-4 transition">
            Browse Brands
          </Link>
          <div
            className="relative py-2"
            onFocus={openInformationMenu}
            onMouseEnter={openInformationMenu}
            onMouseLeave={scheduleInformationMenuClose}
          >
            <button
              aria-expanded={isInformationOpen}
              aria-haspopup="menu"
              className="text-foreground/80 hover:text-foreground hover:underline underline-offset-4 flex items-center gap-1 transition cursor-pointer bg-transparent border-none p-0 text-xs uppercase tracking-widest font-medium"
              onClick={() => setIsInformationOpen((open) => !open)}
              type="button"
            >
              Information
              <ChevronDown className={`w-3 h-3 transition-transform ${isInformationOpen ? "rotate-180" : ""}`} />
            </button>

            {isInformationOpen && (
              <div
                aria-label="Information pages"
                className="information-menu-panel py-1 animate-in fade-in slide-in-from-top-1 duration-150"
                onMouseEnter={openInformationMenu}
                role="menu"
              >
                {informationMenu.map((item) => {
                  const hasChildren = item.children.length > 0;
                  const isSectionOpen = openInformationSection === item.slug;
                  return (
                    <div
                      className="information-menu-item"
                      key={item.slug}
                      onFocus={() => hasChildren && setOpenInformationSection(item.slug)}
                      onMouseEnter={() => setOpenInformationSection(hasChildren ? item.slug : null)}
                    >
                      <Link
                        aria-expanded={hasChildren ? isSectionOpen : undefined}
                        aria-haspopup={hasChildren ? "menu" : undefined}
                        className="information-menu-link"
                        href={item.href}
                        onClick={closeInformationMenu}
                        role="menuitem"
                      >
                        <span>{item.title}</span>
                        {hasChildren && <ChevronRight aria-hidden className="w-3.5 h-3.5 shrink-0" />}
                      </Link>

                      {hasChildren && isSectionOpen && (
                        <div aria-label={`${item.title} pages`} className="information-menu-third-level py-1" role="menu">
                          {item.children.map((child) => (
                            <Link
                              className="information-menu-link"
                              href={child.href}
                              key={child.slug}
                              onClick={closeInformationMenu}
                              role="menuitem"
                            >
                              {child.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <Link href="/flow" className="text-foreground/80 hover:text-foreground hover:underline underline-offset-4 transition">
            Flow
          </Link>
          {canViewAdminMenu && (
            <div
              className="relative py-2"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="text-primary hover:underline underline-offset-4 flex items-center gap-1.5 transition font-bold cursor-pointer text-xs uppercase tracking-widest outline-none bg-transparent border-none p-0"
              >
                <Shield className="w-3.5 h-3.5" />
                Admin
              </button>

              {/* Dropdown Menu */}
              {isAdminOpen && (
                <div
                  className="absolute left-0 mt-2 w-48 bg-background border border-border py-1 shadow-lg z-50 animate-in fade-in slide-in-from-top-1 duration-150"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  {canEditProducts && (
                    <>
                      <Link
                        href="/admin"
                        className="block px-4 py-2.5 text-[10px] uppercase tracking-widest text-foreground hover:bg-secondary hover:text-primary transition font-semibold"
                      >
                        <span className="inline-flex items-center gap-1.5"><SunMoon className="w-3 h-3" /> Product Editor</span>
                      </Link>
                      <Link
                        href="/admin/shipping-rates"
                        className="block px-4 py-2.5 text-[10px] uppercase tracking-widest text-foreground hover:bg-secondary hover:text-primary transition font-semibold"
                      >
                        <span className="inline-flex items-center gap-1.5"><Truck className="w-3 h-3" /> Shipping Rates</span>
                      </Link>
                    </>
                  )}
                  {canEditSiteSettings && (
                    <>
                      <Link
                        href="/admin/site-settings"
                        className="block px-4 py-2.5 text-[10px] uppercase tracking-widest text-foreground hover:bg-secondary hover:text-primary transition font-semibold"
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <Settings className="w-3 h-3" /> Site Settings
                        </span>
                      </Link>
                      <Link
                        href="/admin/campaigns"
                        className="block px-4 py-2.5 text-[10px] uppercase tracking-widest text-foreground hover:bg-secondary hover:text-primary transition font-semibold"
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <Megaphone className="w-3 h-3" /> Campaigns
                        </span>
                      </Link>
                      <Link
                        href="/admin/coupons"
                        className="block px-4 py-2.5 text-[10px] uppercase tracking-widest text-foreground hover:bg-secondary hover:text-primary transition font-semibold"
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <TicketPercent className="w-3 h-3" /> Coupons
                        </span>
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Quick Actions (Theme selector, cart, account, auth) */}
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="hidden sm:block">
            <ThemeSelector />
          </div>

          {/* Cart Icon Link with Badge (Desktop) */}
          <Link
            href="/cart"
            className="relative hidden md:flex text-foreground hover:text-primary transition p-2 cursor-pointer"
            title="Shopping Cart"
          >
            <ShoppingBag className="w-4 h-4" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[8px] font-bold w-4 h-4 flex items-center justify-center font-mono border border-background">
                {cartCount}
              </span>
            )}
          </Link>

          {user && canViewEmail ? (
            <Link
              aria-label="Open email"
              className="relative flex p-2 text-foreground transition hover:text-primary"
              href="/mail"
              title="Email"
            >
              <Mail className="h-4 w-4" />
              {unreadEmailCount > 0 ? (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center bg-blue-600 px-1 font-mono text-[8px] font-bold text-white">
                  {unreadEmailCount > 99 ? "99+" : unreadEmailCount}
                </span>
              ) : null}
            </Link>
          ) : null}

          {user && canManageInquiries ? (
            <Link
              aria-label="Open inquiries"
              className="relative flex p-2 text-foreground transition hover:text-primary"
              href="/admin/inquiries"
              title="Inquiries"
            >
              <MessageSquareText className="h-4 w-4" />
              {newInquiryCount > 0 ? (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center bg-primary px-1 font-mono text-[8px] font-bold text-primary-foreground">
                  {newInquiryCount > 99 ? "99+" : newInquiryCount}
                </span>
              ) : null}
            </Link>
          ) : null}

          <div className="border-l border-border h-6 w-px" />

          {user ? (
            <div className="relative" ref={accountMenuRef}>
              <button
                aria-expanded={isAccountOpen}
                aria-haspopup="menu"
                aria-label="User menu"
                className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold hover:text-primary transition cursor-pointer bg-transparent border-none p-0"
                onClick={() => setIsAccountOpen((open) => !open)}
                type="button"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name || "User"}
                    className="w-6 h-6 border border-border"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "https://media.axiommanifold.com/images/default-avatar.webp";
                    }}
                  />
                ) : (
                  <User className="w-4 h-4 text-foreground/60" />
                )}
                <span className="hidden sm:inline text-foreground max-w-[80px] truncate">{user.name || user.email}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isAccountOpen ? "rotate-180" : ""}`} />
              </button>

              {isAccountOpen && (
                <div
                  className="absolute right-0 top-full mt-3 w-64 bg-background border border-border shadow-xl z-50 py-1"
                  role="menu"
                >
                  <div className="px-4 py-3 border-b border-border">
                    <strong className="block text-xs text-foreground truncate">{user.name || "Signed in"}</strong>
                    <span className="block mt-1 text-[10px] normal-case tracking-normal text-foreground/60 truncate">{user.email}</span>
                  </div>
                  <Link
                    className="flex items-center gap-2 px-4 py-3 text-[10px] uppercase tracking-widest text-foreground hover:bg-secondary hover:text-primary transition font-semibold"
                    href="/profile"
                    onClick={() => setIsAccountOpen(false)}
                    role="menuitem"
                  >
                    <User className="w-3.5 h-3.5" /> Profile
                  </Link>
                  <button
                    className="flex w-full items-center gap-2 px-4 py-3 text-[10px] uppercase tracking-widest text-foreground hover:bg-secondary hover:text-primary transition font-semibold cursor-pointer border-none border-t border-border bg-transparent"
                    onClick={() => {
                      setIsAccountOpen(false);
                      logout();
                    }}
                    role="menuitem"
                    type="button"
                  >
                    <LogOut className="w-3.5 h-3.5" /> Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <button
                onClick={login}
                className="text-xs uppercase tracking-widest font-semibold border border-border hover:border-foreground px-4 py-2 hover:bg-foreground hover:text-background transition-all duration-300 cursor-pointer"
              >
                Sign In
              </button>
              <button
                onClick={signup}
                className="hidden sm:block text-xs uppercase tracking-widest font-semibold border border-border hover:border-foreground px-4 py-2 hover:bg-foreground hover:text-background transition-all duration-300 cursor-pointer"
              >
                Sign Up
              </button>
            </div>
          )}

          {/* Mobile shop cart trigger */}
          <Link href="/cart" className="md:hidden text-foreground hover:text-primary transition relative p-2" title="Cart">
            <ShoppingBag className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute top-0 right-0 bg-primary text-primary-foreground text-[8px] font-bold w-4.5 h-4.5 flex items-center justify-center font-mono border border-background">
                {cartCount}
              </span>
            )}
          </Link>
        </div>
      </div>

      {/* Mobile Information navigation */}
      {!controlPlane && <div className="md:hidden border-t border-border/70">
        <button
          aria-expanded={isMobileInformationOpen}
          className="w-full flex items-center justify-between px-4 py-2.5 text-[10px] uppercase tracking-widest font-bold text-foreground bg-transparent border-none cursor-pointer"
          onClick={() => setIsMobileInformationOpen((open) => !open)}
          type="button"
        >
          Information
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isMobileInformationOpen ? "rotate-180" : ""}`} />
        </button>
        {isMobileInformationOpen && (
          <nav aria-label="Information pages" className="information-menu-mobile-panel px-4 py-2">
            {informationMenu.map((item) => {
              const hasChildren = item.children.length > 0;
              const isSectionOpen = openMobileSection === item.slug;
              return (
                <div className="border-b border-border/40 last:border-b-0" key={item.slug}>
                  <div className="flex items-stretch">
                    <Link
                      className="flex-grow py-3 text-[10px] uppercase tracking-widest font-semibold hover:text-primary"
                      href={item.href}
                      onClick={() => setIsMobileInformationOpen(false)}
                    >
                      {item.title}
                    </Link>
                    {hasChildren && (
                      <button
                        aria-expanded={isSectionOpen}
                        aria-label={`Show ${item.title} pages`}
                        className="px-3 bg-transparent border-none cursor-pointer text-foreground hover:text-primary"
                        onClick={() => setOpenMobileSection(isSectionOpen ? null : item.slug)}
                        type="button"
                      >
                        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${isSectionOpen ? "rotate-90" : ""}`} />
                      </button>
                    )}
                  </div>
                  {hasChildren && isSectionOpen && (
                    <div className="border-l border-primary/40 ml-2 mb-2 pl-3">
                      {item.children.map((child) => (
                        <Link
                          className="block py-2 text-[9px] uppercase tracking-widest text-foreground/75 hover:text-primary"
                          href={child.href}
                          key={child.slug}
                          onClick={() => setIsMobileInformationOpen(false)}
                        >
                          {child.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        )}
      </div>}

      {/* Mobile nav subheader */}
      {controlPlane ? (
        <nav aria-label="Administrative applications" className="control-plane-mobile-nav md:hidden">
          {canEditProducts && <Link href="/admin"><SunMoon />Catalog</Link>}
          {canEditProducts && <Link href="/admin/shipping-rates"><Truck />Shipping</Link>}
          {canEditSiteSettings && <Link href="/admin/site-settings"><Settings />Settings</Link>}
          {canEditSiteSettings && <Link href="/admin/campaigns"><Megaphone />Campaigns</Link>}
          {canEditSiteSettings && <Link href="/admin/coupons"><TicketPercent />Coupons</Link>}
        </nav>
      ) : canViewAdminMenu && (
        <div className="md:hidden bg-primary/10 border-t border-primary/20 text-center py-1.5">
          <Link href={canEditProducts ? "/admin" : "/admin/site-settings"} className="text-[10px] uppercase tracking-widest text-primary font-bold inline-flex items-center gap-1">
            <Shield className="w-3 h-3" /> Admin Portal Authorized
          </Link>
        </div>
      )}
    </header>
  );
};
export default Header;
